import { CheckCircleIcon, XCircleIcon, InfoIcon } from '@phosphor-icons/react'
import { ScoopButton, ScoopChip, ScoopColumn, ScoopRow, ScoopText } from '../../../../ui'
import { getActionScopeLabel } from '../../utils/hierarchyUtils'
import { priorityColor } from '../../utils/indicatorColors'
import { formatDateTime } from '../../utils/formatDate'
import styles from './ActionCandidateCard.module.scss'

// Status is a STATE (chip-legal); priority is metadata (colored text per the
// shared indicator module, review D3).
const STATUS_COLORS = {
    pending: 'warning',
    accepted: 'success',
    rejected: 'error',
}

const unwrapItems = (value) => {
    if (!value) return []
    if (Array.isArray(value)) return value
    if (Array.isArray(value.items)) return value.items
    return []
}



export const ActionCandidateCard = ({
    candidate,
    compact = false,
    onAccept,
    onReject,
    onViewDetails,
    accepting,
    rejecting,
    pendingFingerprint,
}) => {
    const priority = candidate.priority || 'MEDIUM'
    const status = candidate.status || 'pending'
    const guideRefs = unwrapItems(candidate.guideRefs)
    const evidence = unwrapItems(candidate.evidence)
    const accepted = status === 'accepted'
    const rejected = status === 'rejected'
    const lastSeen = formatDateTime(candidate.lastSeenAt)
    const scopeLabel = getActionScopeLabel(candidate)

    // Per-card pending (review D22): only THIS card spins when its own action
    // is in flight; other cards disable instead of all blurring together.
    const isPendingMine = pendingFingerprint === candidate.candidateFingerprint
    const isPendingOther = Boolean(pendingFingerprint) && !isPendingMine

    return (
        <div className={`${styles.card} ${compact ? styles.compact : ''}`}>
            <div className={styles.header}>
                <ScoopColumn className={styles.titleBlock}>
                    <ScoopRow className={styles.metaRow}>
                        <ScoopText
                            className={`${styles.priorityText} ${
                                styles[`priority${priorityColor(priority)}`]
                            }`}
                        >
                            {priority}
                        </ScoopText>
                        <ScoopChip
                            label={status}
                            color={STATUS_COLORS[status] || 'default'}
                            size="small"
                        />
                        {(candidate.source === 'manual' || scopeLabel) && (
                            <ScoopText className={styles.scopeText}>
                                {[candidate.source === 'manual' ? 'manual' : null, scopeLabel]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </ScoopText>
                        )}
                    </ScoopRow>
                    <ScoopText className={styles.title}>
                        {candidate.title || 'Untitled action'}
                    </ScoopText>
                    {candidate.summary && (
                        <ScoopText className={styles.summary}>{candidate.summary}</ScoopText>
                    )}
                </ScoopColumn>
                <ScoopRow className={styles.actions}>
                    {onViewDetails && (
                        <ScoopButton
                            variant="grey"
                            size="xs"
                            onClick={() => onViewDetails(candidate)}
                        >
                            <InfoIcon size={14} />
                            Details
                        </ScoopButton>
                    )}
                    {onAccept && (
                        <ScoopButton
                            variant="grey"
                            size="xs"
                            onClick={() => onAccept(candidate)}
                            disabled={accepted || rejected || isPendingOther}
                            loading={isPendingMine && accepting}
                        >
                            <CheckCircleIcon size={14} />
                            Accept
                        </ScoopButton>
                    )}
                    {onReject && (
                        <ScoopButton
                            variant="grey"
                            size="xs"
                            onClick={() => onReject(candidate)}
                            disabled={rejected || isPendingOther}
                            loading={isPendingMine && rejecting}
                        >
                            <XCircleIcon size={14} />
                            Reject
                        </ScoopButton>
                    )}
                </ScoopRow>
            </div>

            {!compact && (
                <div className={styles.detailGrid}>
                    <div>
                        <ScoopText className={styles.fieldLabel}>Recommended work</ScoopText>
                        {guideRefs.length > 0 ? (
                            <ul className={styles.detailList}>
                                {guideRefs.slice(0, 3).map((guide, index) => (
                                    <li key={`${guide.guideId || index}`}>
                                        {guide.actionText || guide.whyThisOne || guide.guideId}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <ScoopText className={styles.muted}>
                                No catalog actions attached
                            </ScoopText>
                        )}
                    </div>
                    <div>
                        <ScoopText className={styles.fieldLabel}>Evidence</ScoopText>
                        {evidence.length > 0 ? (
                            <ul className={styles.detailList}>
                                {evidence.slice(0, 2).map((item, index) => (
                                    <li key={index}>
                                        {item.primary_cause ||
                                            item.selection_rationale ||
                                            item.note ||
                                            item.source}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <ScoopText className={styles.muted}>No evidence attached</ScoopText>
                        )}
                    </div>
                </div>
            )}

            <ScoopRow className={styles.footer}>
                {lastSeen && (
                    <ScoopText className={styles.footerText}>Last seen {lastSeen}</ScoopText>
                )}
                {candidate.cycleCount > 1 && (
                    <ScoopText className={styles.footerText}>
                        Seen in {candidate.cycleCount} cycles
                    </ScoopText>
                )}
                {candidate.acceptedBy && (
                    <ScoopText className={styles.footerText}>
                        Accepted by {candidate.acceptedBy}
                    </ScoopText>
                )}
            </ScoopRow>
        </div>
    )
}
